/**
 * Credentials API — typed wrappers over /api/credentials/* (M-X9).
 *
 * Lists / creates / patches / rotates / deletes credentials, manages
 * grants, and pulls audit log. Decrypted reveal is a separate endpoint
 * because the gateway audit-logs every call to it.
 */
import { apiJson } from './client';
import { normalizeOrgPublications } from './org-publication-shape';

export type CredentialKind =
  | 'api_key'
  | 'oauth_token'
  | 'webhook_url'
  | 'connection_string'
  | 'cert'
  | 'secret'
  | 'other'
  // M-BB (Secure Browser Broker, Stage 1): website creds. The secret
  // value is the password (website_login) or the cookie blob
  // (website_cookie). These are used by the broker, never injected into
  // agent containers as env vars.
  | 'website_login'
  | 'website_cookie';

export type GranteeType = 'org' | 'board' | 'card' | 'agent_role' | 'user';

// 0066 (credential broker isolation): how the gateway proxy injects the
// secret on an outbound request for an isolated (brokerOnly) API credential.
export type AuthScheme = 'bearer' | 'header' | 'query' | 'basic';

// 0140 — "Secret exposure", the founder-facing control.
//   'protected' — the raw value stays in the gateway. Agents use the credential
//                 by NAME (browser_open / api_request) when its kind has a proxy
//                 path, and cannot use it at all when it does not.
//   'exposed'   — the raw value is written into the environment of every agent
//                 container the credential is granted to.
// This is the EFFECTIVE state: the gateway folds an elapsed time box into it, so
// a credential whose exposure has run out reports 'protected' here without any
// background job having touched the row. Never derive exposure from
// `brokerOnly` in the UI — it is the raw storage column and ignores the box.
export type CredentialExposure = 'protected' | 'exposed';

// 0138 — the gateway's own upstream keys (KB embedding, the model list, STT).
// Non-null means CHAOS consumes this credential itself; no agent receives it in
// either mode and the exposure control does not apply.
export type PlatformService = string;

export interface CredentialMeta {
  id: string;
  // 0141: ADVISORY provenance — the org the credential was created in. Nullable:
  // deleting that org nulls the pointer and the credential survives. Ownership
  // is accountId. Never gate anything on orgId.
  orgId: string | null;
  accountId: string | null;
  name: string;
  description: string | null;
  kind: CredentialKind;
  envVarName: string;
  // M-BB: plaintext non-secret metadata for website creds (null otherwise).
  siteUrl: string | null;
  loginUsername: string | null;
  // 0066: raw storage column. Kept for existing consumers ONLY — read
  // `exposure` instead, which also accounts for an elapsed time box.
  brokerOnly: boolean;
  authScheme: AuthScheme | null;
  authParam: string | null;
  // 0140: the Secret exposure control. `exposure` is effective state;
  // `exposureExpiresAt` is the time box (null = no automatic revert);
  // `exposureReason` is the mandatory note given when it was last exposed.
  exposure: CredentialExposure;
  exposureExpiresAt: string | null;
  exposureReason: string | null;
  exposureSetAt: string | null;
  exposureSetBy: string | null;
  // 0138: non-null = the gateway's own key for that upstream service. The
  // exposure control is disabled for these.
  platformService: PlatformService | null;
  // Wave 4 (0067): broker-managed OAuth auto-refresh metadata (non-secret).
  // oauthRefreshable=true → the gateway owns token rotation (the stored secret
  // is a refresh bundle). tokenUrl = the OAuth token endpoint. The refresh
  // token / client secret are NEVER returned by the API.
  tokenUrl: string | null;
  oauthExpiresAt: string | null;
  oauthRefreshable: boolean;
  // Wave 5 (0068): OAuth re-auth human-handoff metadata (non-secret). The
  // authorize URL / scope / redirect URI are the founder's OAuth-app config;
  // reauthStatus is 'needed' when a refresh died and a human must Reconnect
  // (else null). reauthState is NEVER returned (server-side only).
  authorizeUrl: string | null;
  oauthScope: string | null;
  oauthRedirectUri: string | null;
  reauthStatus: 'needed' | null;
  reauthRequestedAt: string | null;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  lastAccessedAt: string | null;
  accessCount: number;
}

// Cross-Workspace sharing (0073, reshaped by migration 0166).
//
//   orgIds       — every Workspace this credential is granted in. THE list.
//   viaAllOrgIds — the subset that the "All Workspaces" select-all button put
//                  there (⊆ orgIds). The origin is remembered because two rules
//                  depend on it: a new solo Workspace inherits these marks, and
//                  the first other person added to a Workspace switches them
//                  off. Marks placed by hand are never touched by either.
//
// There is no `allOrgs` boolean any more. "Available everywhere" used to be a
// separate STATUS row (org_id IS NULL) which made it impossible to exclude a
// single Workspace; it is now simply the state where every Workspace is ticked,
// which the UI reads off `orgIds` and therefore cannot disagree about.
export interface CredentialShares {
  orgIds: string[];
  viaAllOrgIds: string[];
}

/**
 * Make both lists REAL, whatever the gateway actually put on the wire.
 *
 * `CredentialShares` above is a TypeScript interface, and TypeScript is not on
 * the wire. This JSON comes from a gateway that is built, baked and restarted
 * SEPARATELY from this bundle — /srv/chaos/deploy-log.jsonl routinely shows the
 * two bakes landing tens of minutes apart — and a gateway from before migration
 * 0166 answers `{ allOrgs, orgIds }`, with no `viaAllOrgIds` at all.
 *
 * WHAT NOT DOING THIS COST, 2026-08-01 → 2026-08-02. Every reader treats
 * `viaAllOrgIds` as an array it may call `.includes()` on. One absent field
 * therefore threw `TypeError: Cannot read properties of undefined (reading
 * 'includes')` out of credential-detail-v2's Grants tab — which React evaluates
 * while building the element tree no matter which tab is open — and the
 * settings error boundary ate the whole section. Opening ANY credential, on ANY
 * device, read "This section failed to load", for six commits.
 *
 * `?? { orgIds: [], viaAllOrgIds: [] }` at the read site did not help and could
 * not: it defends against an ABSENT `shares` object, and what arrived was a
 * PRESENT object with a missing field. That distinction is the whole defect.
 *
 * So it is done ONCE, here, at the seam where JSON becomes objects — not at the
 * four call sites, which works until the fifth one forgets. `Array.isArray` and
 * not `??` on purpose: `null`, a string, and an object all have to land on `[]`
 * too, and only one of those three is what a nullish coalesce catches.
 *
 * THE BODY MOVED TO ./org-publication-shape, and this stayed a named export.
 * Migration 0168 reshaped PUBLICATIONS on skills, connectors, templates,
 * permissions, roles and approval rules the same way 0166 reshaped these
 * shares, so the same guard is now owed at six more `listAll()` seams. Two
 * copies of it would be two things to keep in step, which is the shape of the
 * original defect one level up. The name survives because callers and
 * tests/unit/credential-shares-wire-shape.test.ts read the credentials story
 * off it, and that story is where this cost sixteen hours.
 */
export function normalizeShares(
  shares: Partial<CredentialShares> | null | undefined,
): CredentialShares {
  return normalizeOrgPublications(shares);
}

// One column of the cross-org matrix — an org that belongs to the caller's
// account. `logoUrl` drives the square monogram/logo header cell.
export interface CredentialMatrixOrg {
  id: string;
  displayName: string;
  logoUrl: string | null;
}

// One row of the cross-org matrix (GET /api/credentials/all). A trimmed
// CredentialMeta (no secret cols, no OAuth/website plumbing) plus the owner
// org id and the share set. The owner org always implicitly "has" the cred.
export interface CredentialMatrixRow {
  id: string;
  name: string;
  description: string | null;
  kind: CredentialKind;
  envVarName: string;
  brokerOnly: boolean;
  // 0140 — what the Exposure column and the "Exposed only" filter read.
  exposure: CredentialExposure;
  exposureExpiresAt: string | null;
  // 0138 — rendered as `Protected · platform key`; the toggle is disabled.
  platformService: PlatformService | null;
  reauthStatus: 'needed' | null;
  version: number;
  createdAt: string;
  lastAccessedAt: string | null;
  accessCount: number;
  // 0141: advisory, and null once the org it was created in is deleted. The
  // credential itself is owned by the account and is still listed here.
  ownerOrgId: string | null;
  shares: CredentialShares;
}

export interface CredentialMatrix {
  credentials: CredentialMatrixRow[];
  orgs: CredentialMatrixOrg[];
}

export interface CredentialReveal {
  id: string;
  name: string;
  kind: CredentialKind;
  envVarName: string;
  value: string;
  version: number;
}

// A grant row. `orgId` (migration 0167) is the Workspace an `agent_role` or
// `user` grant was made in — it is NULL for org / board / card, which carry
// their Workspace in `granteeId` already. Two rows differing only in `orgId`
// are two grants, not a duplicate: that is the whole point of the column.
export interface CredentialGrant {
  credentialId: string;
  granteeType: GranteeType;
  granteeId: string;
  orgId: string | null;
  grantedBy: string | null;
  grantedAt: string;
}

/**
 * WHERE a role/user grant applies. There is no default, deliberately: the
 * gateway answers 400 rather than guessing a Workspace, so this type makes the
 * caller state it.
 *
 *   { orgIds }            — these Workspaces, named.
 *   { orgScope: 'shared' } — every Workspace the credential is currently shared
 *                            into. What a grant made from the GLOBAL role
 *                            editor reached before 0167, written as explicit
 *                            rows so any one can be taken back on its own.
 */
export type GrantWorkspaceScope = { orgIds: string[] } | { orgScope: 'shared' };

/** WHERE a revoke applies. `{ orgScope: 'all' }` takes the grant out everywhere. */
export type RevokeWorkspaceScope = { orgId: string } | { orgScope: 'all' };

export interface CredentialAuditEntry {
  id: string;
  accessedByKind: 'user' | 'agent' | 'system';
  accessedById: string | null;
  accessedByEmail: string | null;
  sessionId: string | null;
  cardId: string | null;
  accessedAt: string;
  action: string;
  context: unknown;
}

interface ListResp {
  credentials: CredentialMeta[];
}
interface SharesResp {
  shares: CredentialShares;
}
interface OneResp {
  credential: CredentialMeta;
}
interface GrantsResp {
  grants: CredentialGrant[];
}
interface AuditResp {
  entries: CredentialAuditEntry[];
}

export interface CreateCredentialInput {
  orgId: string;
  name: string;
  value: string;
  description?: string | null;
  kind: CredentialKind;
  envVarName?: string | null;
  // M-BB: plaintext website metadata (siteUrl required for website kinds).
  siteUrl?: string | null;
  loginUsername?: string | null;
  // 0140: `brokerOnly` is GONE from the create input. Every new credential is
  // created Protected, server-side and unconditionally. Exposure is a separate,
  // deliberate action from the detail sheet, where the confirmation dialog and
  // the mandatory reason note live.
  authScheme?: AuthScheme | null;
  authParam?: string | null;
  // Wave 4 (0067): OAuth auto-refresh config — only for an isolated
  // oauth_token cred. All four sent together; the refresh material is folded
  // into the encrypted bundle gateway-side and never stored in plaintext.
  tokenUrl?: string | null;
  refreshToken?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  // Wave 5 (0068): OAuth re-auth handoff config (non-secret OAuth-app metadata).
  authorizeUrl?: string | null;
  oauthScope?: string | null;
  oauthRedirectUri?: string | null;
}

export interface UpdateCredentialInput {
  name?: string;
  description?: string | null;
  kind?: CredentialKind;
  envVarName?: string | null;
  value?: string;
  siteUrl?: string | null;
  loginUsername?: string | null;
  // 0140: `brokerOnly` is GONE from the patch input too — the gateway now 400s
  // on it. Exposure is changed through setExposure() below, so the reason note
  // is mandatory and the audit row cannot be skipped.
  authScheme?: AuthScheme | null;
  authParam?: string | null;
  // Wave 4 (0067): OAuth auto-refresh config (isolated oauth_token only).
  tokenUrl?: string | null;
  refreshToken?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  // Wave 5 (0068): OAuth re-auth handoff config (non-secret OAuth-app metadata).
  authorizeUrl?: string | null;
  oauthScope?: string | null;
  oauthRedirectUri?: string | null;
}

// Wave 5 (0068): response from the Reconnect endpoint — the provider AUTHORIZE
// URL the founder opens to re-authorise (non-secret) + the redirect_uri used.
export interface ReauthUrlResp {
  authorizeUrl: string;
  redirectUri: string;
}

export const credentialsApi = {
  list(orgId: string, kind?: CredentialKind): Promise<CredentialMeta[]> {
    const params = new URLSearchParams({ orgId });
    if (kind) params.set('kind', kind);
    return apiJson<ListResp>(`/credentials?${params.toString()}`).then((r) => r.credentials);
  },
  // 0073: credentials AVAILABLE in an org (owned OR shared into it, incl. the
  // all-orgs sentinel) — `&grantable=1`. Powers the role credential-grant
  // picker, which must offer shared creds, not only the org's own. Distinct
  // from list() so its react-query cache (credentialsKeys.grantable) never
  // collides with the owned-only list used elsewhere.
  listGrantable(orgId: string, kind?: CredentialKind): Promise<CredentialMeta[]> {
    const params = new URLSearchParams({ orgId, grantable: '1' });
    if (kind) params.set('kind', kind);
    return apiJson<ListResp>(`/credentials?${params.toString()}`).then((r) => r.credentials);
  },
  // 0073: account-wide matrix — every credential whose owner org belongs to
  // the caller's account, each with its share set, plus the account's orgs as
  // the matrix columns. Account-owner only (403 otherwise).
  //
  // THE SHARE LISTS ARE NORMALISED AT THE DOOR (see normalizeShares). This is
  // the only route that puts a share list into the cache, so every later reader
  // — the matrix cells, the "All Workspaces" header box, the detail slide-over
  // — gets both arrays whatever version of the gateway answered. Take it out
  // and the settings section dies on any pre-0166 payload; that is exactly what
  // tests/browser-proof-settings goes red on.
  listAll(): Promise<CredentialMatrix> {
    return apiJson<CredentialMatrix>(`/credentials/all`).then((m) => ({
      ...m,
      credentials: m.credentials.map((c) => ({ ...c, shares: normalizeShares(c.shares) })),
    }));
  },
  get(id: string): Promise<CredentialMeta> {
    return apiJson<OneResp>(`/credentials/${id}`).then((r) => r.credential);
  },
  reveal(id: string): Promise<CredentialReveal> {
    return apiJson<CredentialReveal>(`/credentials/${id}/reveal`);
  },
  create(input: CreateCredentialInput): Promise<CredentialMeta> {
    return apiJson<OneResp>(`/credentials`, { method: 'POST', json: input }).then(
      (r) => r.credential,
    );
  },
  update(id: string, input: UpdateCredentialInput): Promise<CredentialMeta> {
    return apiJson<OneResp>(`/credentials/${id}`, { method: 'PATCH', json: input }).then(
      (r) => r.credential,
    );
  },
  // 0140 — set the Secret exposure of ONE credential.
  //
  //   exposed:   true = Exposed (raw value into agent containers from the next
  //              spawn), false = Protected.
  //   expiresAt: ISO 8601, at most 30 days out. null = "keep exposed until I
  //              turn it off". Ignored when exposed = false.
  //   reason:    REQUIRED when exposing — what will consume the raw value. It
  //              is stored on the credential and copied into the audit log.
  //
  // The gateway rejects exposing a platform key or a website credential, so the
  // UI must disable the control for those rather than surface a 400.
  setExposure(
    id: string,
    input: { exposed: boolean; expiresAt?: string | null; reason?: string },
  ): Promise<CredentialMeta> {
    return apiJson<OneResp>(`/credentials/${id}/exposure`, {
      method: 'PUT',
      // expiresAt is ALWAYS sent, explicitly — the gateway rejects an exposing
      // call that omits it, so "no automatic revert" is a choice this client
      // states rather than a field it happened not to include.
      json: {
        exposed: input.exposed,
        expiresAt: input.expiresAt ?? null,
        reason: input.reason ?? null,
      },
    }).then((r) => r.credential);
  },
  /**
   * "What is open right now, and to whom." Deliberately NOT derived from the
   * matrix: an exposure whose time box has run out already reads 'protected'
   * there, so the window in which the containers still hold the value is
   * invisible to every list in the app. This endpoint reports the raw columns.
   */
  liveExposures() {
    return apiJson<LiveExposuresResp>(`/credentials/exposure/live`);
  },

  rotate(id: string, value: string): Promise<CredentialMeta> {
    return apiJson<OneResp>(`/credentials/${id}/rotate`, {
      method: 'POST',
      json: { value },
    }).then((r) => r.credential);
  },
  // Wave 5 (0068): build the provider AUTHORIZE URL the founder opens to
  // Reconnect a credential whose OAuth refresh died (or proactively). The
  // gateway mints + stores a fresh state; no secret is returned.
  reauthUrl(id: string): Promise<ReauthUrlResp> {
    return apiJson<ReauthUrlResp>(`/credentials/${id}/oauth/reauth-url`, {
      method: 'POST',
      json: {},
    });
  },
  remove(id: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/credentials/${id}`, { method: 'DELETE' });
  },
  listGrants(id: string): Promise<CredentialGrant[]> {
    return apiJson<GrantsResp>(`/credentials/${id}/grants`).then((r) => r.grants);
  },
  // 0066: role-scoped grant list — the credential ids granted to one agent
  // role (grantee_type='agent_role') within an org. Powers the role editor.
  listGrantedToRole(orgId: string, roleId: string): Promise<string[]> {
    const params = new URLSearchParams({ orgId, roleId });
    return apiJson<{ credentialIds: string[] }>(
      `/credentials/grants/by-role?${params.toString()}`,
    ).then((r) => r.credentialIds);
  },
  // Global-role variants — credentials live in orgs, but a GLOBAL role has no
  // org, so the org-scoped grantable/by-role endpoints can't be used for it.
  // These account-scoped (system-founder gated) endpoints list every credential
  // owned by the account's orgs (so the all-orgs-shared founder creds all
  // appear), and the credential ids granted to one global role id. The grant
  // itself is identical (agent_role grantee + the global role id); only the
  // listing path differs.
  listGrantableForGlobal(kind?: CredentialKind): Promise<CredentialMeta[]> {
    const params = new URLSearchParams();
    if (kind) params.set('kind', kind);
    const q = params.toString();
    return apiJson<ListResp>(`/credentials/grantable-global${q ? `?${q}` : ''}`).then(
      (r) => r.credentials,
    );
  },
  listGrantedToGlobalRole(roleId: string): Promise<string[]> {
    const params = new URLSearchParams({ roleId });
    return apiJson<{ credentialIds: string[] }>(
      `/credentials/grants/by-global-role?${params.toString()}`,
    ).then((r) => r.credentialIds);
  },
  // `scope` is REQUIRED for agent_role / user and must be omitted for
  // org / board / card — the gateway 400s either way round, so a caller that
  // gets it wrong hears about it instead of writing a grant nobody chose.
  addGrant(
    id: string,
    granteeType: GranteeType,
    granteeId: string,
    scope?: GrantWorkspaceScope,
  ): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/credentials/${id}/grants`, {
      method: 'POST',
      json: { granteeType, granteeId, ...(scope ?? {}) },
    });
  },
  removeGrant(
    id: string,
    granteeType: GranteeType,
    granteeId: string,
    scope?: RevokeWorkspaceScope,
  ): Promise<{ ok: boolean }> {
    const qs =
      scope === undefined
        ? ''
        : 'orgId' in scope
          ? `?orgId=${encodeURIComponent(scope.orgId)}`
          : '?orgScope=all';
    return apiJson<{ ok: boolean }>(
      `/credentials/${id}/grants/${granteeType}/${granteeId}${qs}`,
      { method: 'DELETE' },
    );
  },
  audit(id: string, limit = 100, offset = 0): Promise<CredentialAuditEntry[]> {
    return apiJson<AuditResp>(
      `/credentials/${id}/audit?limit=${limit}&offset=${offset}`,
    ).then((r) => r.entries);
  },
  // 0073/0166: cross-Workspace sharing.
  // Reconcile the FULL Workspace list in one call. Body { orgIds } → the server
  // wipes & re-inserts, every row MANUAL. (Not used by a screen today; the
  // matrix drives single cells and the select-all routes below.)
  setShares(id: string, orgIds: string[]): Promise<CredentialShares> {
    return apiJson<SharesResp>(`/credentials/${id}/shares`, {
      method: 'PUT',
      json: { orgIds },
    }).then((r) => r.shares);
  },
  // Tick ONE Workspace. The mark is MANUAL — a person ticked this box, so the
  // first-member rule must never take it away.
  addShare(id: string, orgId: string): Promise<CredentialShares> {
    return apiJson<SharesResp>(`/credentials/${id}/shares`, {
      method: 'POST',
      json: { orgId },
    }).then((r) => r.shares);
  },
  // Untick ONE Workspace. Works the same whether the mark was hand-placed or
  // came from select-all — that is the point of the 0166 change.
  removeShare(id: string, orgId: string): Promise<CredentialShares> {
    return apiJson<SharesResp>(`/credentials/${id}/shares?orgId=${encodeURIComponent(orgId)}`, {
      method: 'DELETE',
    }).then((r) => r.shares);
  },
  // Convenience: flip one cell to the desired state (used by the matrix
  // checkbox handler — optimistic call site decides on/off).
  toggleShare(id: string, orgId: string, on: boolean): Promise<CredentialShares> {
    return on ? this.addShare(id, orgId) : this.removeShare(id, orgId);
  },
  // "All Workspaces" — the select-all box above the per-Workspace tick-boxes.
  // Ticks every Workspace of the account; rows it creates are stamped as
  // coming from select-all, which is what makes the inherit/revoke rules
  // possible. Marks already there keep their origin.
  selectAllWorkspaces(id: string): Promise<CredentialShares> {
    return apiJson<SharesResp>(`/credentials/${id}/shares/all`, { method: 'POST' }).then(
      (r) => r.shares,
    );
  },
  // The same box, unticked: removes ONLY the marks select-all made. A tick the
  // user placed on one Workspace by hand is a statement about that Workspace
  // and survives — the bulk control undoes bulk work. Survivors leave the
  // header box half-selected, which is how the screen says so.
  clearAllWorkspaces(id: string): Promise<CredentialShares> {
    return apiJson<SharesResp>(`/credentials/${id}/shares/all`, { method: 'DELETE' }).then(
      (r) => r.shares,
    );
  },
};


// ── 0140 second pass — the exposure review surface ──────────────────────────
/**
 * One session that CURRENTLY holds the raw value. Resolved by the gateway
 * through the same selector that decides who gets rebuilt when a credential
 * changes, so "who has it" and "who gets told" can never disagree.
 */
export interface ExposureHolder {
  sessionId: string;
  cardId: string | null;
  cardTitle: string | null;
  orgName: string | null;
  state: string | null;
  lastActiveAt: string | null;
}

export interface LiveExposure {
  id: string;
  name: string;
  kind: CredentialKind;
  envVarName: string;
  /**
   * 'open'   — exposed, box still running (or no box at all).
   * 'lapsed' — the box ran out. `exposure` on the LIST already reads
   *            'protected' for this row, which is why the list cannot show it:
   *            the containers still hold the value and the record has not been
   *            flipped back yet. This is the one state the badge is blind to.
   */
  state: 'open' | 'lapsed';
  expiresAt: string | null;
  reason: string | null;
  setAt: string | null;
  setBy: string | null;
  holders: ExposureHolder[];
}

export interface ExposureSweeperHealth {
  enabled: boolean;
  tickMs: number;
  lastTickAt: string | null;
  lastCleanTickAt: string | null;
  consecutiveFailures: number;
  lastError: string | null;
  boxesClosed: number;
  sessionsMarked: number;
  lastStallSeconds: number | null;
}

export interface LiveExposuresResp {
  exposures: LiveExposure[];
  /** The gateway's single definition of "closed". Never restate it in the UI. */
  closedMeans: string;
  sweeper: ExposureSweeperHealth;
}

export const credentialsKeys = {
  all: ['credentials'] as const,
  list: (orgId: string, kind?: string) => ['credentials', 'list', orgId, kind ?? 'all'] as const,
  // 0073: org-available credential list (owned + shared) — distinct cache from
  // the owned-only `list` key so the role picker does not collide with it.
  grantable: (orgId: string, kind?: string) =>
    ['credentials', 'grantable', orgId, kind ?? 'all'] as const,
  // 0073: account-wide matrix (GET /credentials/all).
  matrix: () => ['credentials', 'matrix'] as const,
  // The live exposure review (GET /credentials/exposure/live).
  liveExposures: () => ['credentials', 'exposure', 'live'] as const,
  detail: (id: string) => ['credentials', 'detail', id] as const,
  grants: (id: string) => ['credentials', 'grants', id] as const,
  grantsByRole: (orgId: string, roleId: string) =>
    ['credentials', 'grants', 'by-role', orgId, roleId] as const,
  // Global-role credential grant caches (account-scoped, no org).
  grantableGlobal: (kind?: string) =>
    ['credentials', 'grantable-global', kind ?? 'all'] as const,
  grantsByGlobalRole: (roleId: string) =>
    ['credentials', 'grants', 'by-global-role', roleId] as const,
  audit: (id: string) => ['credentials', 'audit', id] as const,
};
